import { isAuditBrowser } from './auditBrowser';

type AmplitudeModule = typeof import('@amplitude/unified');

const AMPLITUDE_API_KEY = 'ff2bf72c152fd895cb7f98bd7214352c';

// Amplitude's unified SDK bundles session replay (rrweb), which is a few hundred
// KiB that nothing on the critical path needs. It only downloads once the user
// shows real engagement, so a cold first paint never pays for it. Events made
// before it resolves are queued rather than dropped.
let instance: AmplitudeModule | null = null;
let loadPromise: Promise<AmplitudeModule | null> | null = null;
let scheduled = false;
let pending: Array<(amplitude: AmplitudeModule) => void> = [];
let userId: string | undefined;
let userIdPending = false;
let teardownTriggers: (() => void) | null = null;

function load(): Promise<AmplitudeModule | null> {
  if (loadPromise) return loadPromise;
  loadPromise = import('@amplitude/unified')
    .then(amplitude => {
      amplitude.initAll(AMPLITUDE_API_KEY, {
        analytics: { autocapture: true },
        sessionReplay: { sampleRate: 1 },
      });
      instance = amplitude;
      if (userIdPending) {
        userIdPending = false;
        amplitude.setUserId(userId);
      }
      const queued = pending;
      pending = [];
      queued.forEach(run => {
        try {
          run(amplitude);
        } catch {
          /* analytics must never break the app */
        }
      });
      return amplitude;
    })
    .catch(() => null);
  return loadPromise;
}

function scheduleLoad() {
  if (scheduled || typeof window === 'undefined') return;
  if (isAuditBrowser()) return;
  scheduled = true;
  teardownTriggers?.();
  void load();
}

function enqueue(run: (amplitude: AmplitudeModule) => void) {
  if (instance) {
    try {
      run(instance);
    } catch {
      /* analytics must never break the app */
    }
    return;
  }
  // Queue only — do not force a load. Events flush once a real engagement trigger
  // (set up in initAnalytics) pulls the SDK, keeping the initial load lightweight.
  pending.push(run);
}

export function initAnalytics() {
  if (typeof window === 'undefined' || scheduled || isAuditBrowser()) return;

  // First genuine engagement pulls the SDK. scroll/mousemove are intentionally
  // omitted: Lighthouse synthesizes them and would re-eager the ~900 KiB chunk.
  const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'click'];
  const onInteraction = () => scheduleLoad();
  interactionEvents.forEach(type =>
    window.addEventListener(type, onInteraction, { once: true, passive: true })
  );

  // A visitor who reads without interacting is still captured when they leave
  // the tab — never during the initial page load.
  const onHidden = () => {
    if (document.visibilityState === 'hidden') scheduleLoad();
  };
  const onPageHide = () => scheduleLoad();
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);

  teardownTriggers = () => {
    interactionEvents.forEach(type => window.removeEventListener(type, onInteraction));
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
    teardownTriggers = null;
  };
}

export function trackAmplitudeEvent(eventType: string, eventProperties?: Record<string, string | number | boolean>) {
  enqueue(amplitude => amplitude.track(eventType, eventProperties));
}

export function setAnalyticsUserId(id: string | undefined) {
  userId = id;
  if (instance) {
    instance.setUserId(id);
    return;
  }
  userIdPending = true;
}
